import { useRecoilState } from 'recoil'

import { modalsState } from '@/recoil/modal/atoms'
import { usePathname } from 'next/navigation'
import { ReactNode, useCallback, useEffect } from 'react'

export type modalType = 'Confirm' | 'Alert'

export interface Modal {
  type: modalType
  pathname: string
  props: ModalProps
}

export interface ModalProps {
  id?: number | string
  title?: string
  message?: string | ReactNode
  leftBtn?: {
    label?: string
    callback?: () => void
  }
  rightBtn?: {
    label?: string
    callback?: () => void
  }
  close?: () => void
}

interface ModalStore {
  modalList: Modal[]
  alertModal: (props: Omit<ModalProps, 'leftBtn'>) => void
  confirmModal: (props: ModalProps) => void
  modalClose: (_modal: Modal, callback?: () => void) => void
  lastModalClose: () => void
}

export const useModals = (): ModalStore => {
  const pathname = usePathname()
  const [modalList, setModalList] = useRecoilState(modalsState)

  const alertModal = useCallback(
    (props: ModalProps = {}) => {
      setModalList(list => {
        if (props.id !== undefined && list.some(({ props: { id } }) => id === props.id)) {
          return list.map(item => ({
            type: 'Alert',
            pathname,
            props: item.props.id === props.id ? props : item.props,
          }))
        } else {
          const newList = [...list]
          newList.push({ type: 'Alert', pathname, props })
          return newList
        }
      })
    },
    [pathname, setModalList]
  )

  const confirmModal = useCallback(
    (props: ModalProps = {}) => {
      setModalList(list => {
        if (props.id !== undefined && list.some(({ props: { id } }) => id === props.id)) {
          return list.map(item => ({
            type: 'Confirm',
            pathname,
            props: item.props.id === props.id ? props : item.props,
          }))
        } else {
          const newList = [...list]
          newList.push({ type: 'Confirm', pathname, props })
          return newList
        }
      })
    },
    [pathname, setModalList]
  )

  const modalClose = useCallback(
    (modal: Modal, callback?: () => void) => {
      const closeCallback = modal.props?.close

      setModalList(list => {
        const newList = [...list]
        return newList.filter(_modal => _modal !== modal)
      })
      callback && callback()
      closeCallback && closeCallback()
    },
    [setModalList]
  )

  const lastModalClose = useCallback(() => {
    setModalList(list => {
      return list.slice(0, -1)
    })
  }, [setModalList])

  useEffect(() => {
    setModalList(list => {
      return list.filter(modal => modal.pathname === pathname)
    })
  }, [pathname, setModalList])

  return { modalList, alertModal, confirmModal, modalClose, lastModalClose }
}
